import {
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { cn } from '../lib/utils';

import type { DataTablePaginationLabels, DataTableSelectionLabels } from './data-table.types';

const DEFAULT_PAGINATION_LABELS: Required<DataTablePaginationLabels> = {
  previous: 'Előző oldal',
  next: 'Következő oldal',
  pageOf: (page, total) => `${page} / ${total} oldal`,
  rowsTotal: count => `${count} találat`,
  pageSizeLabel: 'Sor/oldal',
};

const DEFAULT_SELECTION_LABELS: Required<DataTableSelectionLabels> = {
  selectAll: 'Összes kijelölése',
  selectRow: 'Sor kijelölése',
};

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  // --- server-mode ---
  rowCount?: number;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualPagination?: boolean;
  manualSorting?: boolean;
  // --- állapot ---
  isLoading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  // --- interakció ---
  onRowClick?: (row: TData) => void;
  rowActions?: (row: TData) => React.ReactNode;
  /**
   * Per-sor kibontható tartalom (expandable sub-row). Ha jelen van és nem-null-t
   * ad vissza az adott sorra, a DataTable a sor alá egy teljes-szélességű
   * (`colSpan`) sort renderel a visszaadott tartalommal. Az expand-state a
   * fogyasztónál él (state-less DataTable). Additív — alapból kikapcsolt.
   */
  renderSubRow?: (row: TData) => React.ReactNode;
  /** Per-sor CSS-osztály az adat alapján (consumer-driven state-styling, pl. low-stock kiemelés). */
  rowClassName?: (row: TData) => string;
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  getRowId?: (row: TData) => string;
  // --- responsive ---
  mobileCard?: (row: TData) => React.ReactNode;
  // --- feliratok (i18n a hívónál) ---
  paginationLabels?: DataTablePaginationLabels;
  selectionLabels?: DataTableSelectionLabels;
  /** Default oszlop-szám a colSpan-hez (skeleton/empty). */
  className?: string;
}

const SELECT_COLUMN_ID = '__select__';
const ACTIONS_COLUMN_ID = '__actions__';

export function DataTable<TData, TValue>({
  columns,
  data,
  rowCount,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  manualPagination = rowCount !== undefined,
  manualSorting = false,
  isLoading = false,
  error = null,
  emptyMessage = 'Nincs megjeleníthető adat',
  onRowClick,
  rowActions,
  renderSubRow,
  rowClassName,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  mobileCard,
  paginationLabels,
  selectionLabels,
  className,
}: DataTableProps<TData, TValue>) {
  const pageLabels = { ...DEFAULT_PAGINATION_LABELS, ...paginationLabels };
  const selLabels = { ...DEFAULT_SELECTION_LABELS, ...selectionLabels };

  const resolvedColumns = React.useMemo(() => {
    const cols: ColumnDef<TData, TValue>[] = [];
    if (enableRowSelection) {
      cols.push({
        id: SELECT_COLUMN_ID,
        enableSorting: false,
        header: ({ table }) => (
          <input
            type="checkbox"
            aria-label={selLabels.selectAll}
            checked={table.getIsAllRowsSelected()}
            ref={el => {
              if (el) el.indeterminate = table.getIsSomeRowsSelected();
            }}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label={selLabels.selectRow}
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
            onClick={e => e.stopPropagation()}
          />
        ),
      } as ColumnDef<TData, TValue>);
    }
    cols.push(...columns);
    if (rowActions) {
      cols.push({
        id: ACTIONS_COLUMN_ID,
        enableSorting: false,
        header: () => null,
        cell: ({ row }) => (
          <div
            className="flex justify-end"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
          >
            {rowActions(row.original)}
          </div>
        ),
      } as ColumnDef<TData, TValue>);
    }
    return cols;
  }, [columns, enableRowSelection, rowActions, selectionLabels]);

  const state: Record<string, unknown> = {};
  if (sorting !== undefined) state.sorting = sorting;
  if (pagination !== undefined) state.pagination = pagination;
  if (rowSelection !== undefined) state.rowSelection = rowSelection;

  const table = useReactTable({
    data,
    columns: resolvedColumns,
    getCoreRowModel: getCoreRowModel(),
    ...(manualSorting ? { manualSorting: true } : { getSortedRowModel: getSortedRowModel() }),
    ...(manualPagination
      ? { manualPagination: true, rowCount: rowCount ?? 0 }
      : { getPaginationRowModel: getPaginationRowModel() }),
    enableRowSelection,
    ...(getRowId ? { getRowId } : {}),
    state,
    ...(onSortingChange ? { onSortingChange } : {}),
    ...(onPaginationChange ? { onPaginationChange } : {}),
    ...(onRowSelectionChange ? { onRowSelectionChange } : {}),
  });

  const colCount = resolvedColumns.length;
  const showPagination =
    pagination !== undefined || rowCount !== undefined || onPaginationChange !== undefined;
  // Sort-UI csak akkor jelenik meg, ha a fogyasztó opt-in-el (sorting/onSortingChange).
  // Default: plain fejlécek (0 vizuális churn a migrált oldalakon).
  const sortingEnabled = sorting !== undefined || onSortingChange !== undefined;

  function handleRowKeyDown(e: React.KeyboardEvent, original: TData) {
    if (!onRowClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick(original);
    }
  }

  const mobileRows = table.getRowModel().rows;

  return (
    <div className={cn('w-full', className)}>
      <div className={mobileCard ? 'hidden lg:block' : undefined}>
        <Table role="grid" aria-rowcount={rowCount ?? data.length}>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id} role="row">
                {headerGroup.headers.map(header => {
                  const canSort = sortingEnabled && header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const ariaSort =
                    sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined;
                  return (
                    <TableHead key={header.id} aria-sort={ariaSort} role="columnheader">
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 hover:text-foreground"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === 'asc' ? (
                            <ArrowUp className="h-3 w-3" aria-hidden />
                          ) : sorted === 'desc' ? (
                            <ArrowDown className="h-3 w-3" aria-hidden />
                          ) : (
                            <ChevronsUpDown className="h-3 w-3 opacity-50" aria-hidden />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow role="row">
                <TableCell colSpan={colCount}>
                  <div role="status" aria-live="polite" className="space-y-2 py-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="h-6 w-full animate-pulse rounded bg-muted" />
                    ))}
                    <span className="sr-only">Betöltés…</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow role="row">
                <TableCell colSpan={colCount} className="py-8 text-center text-destructive">
                  {error}
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow role="row">
                <TableCell colSpan={colCount} className="py-8 text-center text-muted-foreground">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row: Row<TData>) => {
                const subRow = renderSubRow?.(row.original);
                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      role="row"
                      data-state={row.getIsSelected() ? 'selected' : undefined}
                      tabIndex={onRowClick ? 0 : undefined}
                      className={cn(onRowClick && 'cursor-pointer', rowClassName?.(row.original))}
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                      onKeyDown={onRowClick ? e => handleRowKeyDown(e, row.original) : undefined}
                    >
                      {row.getVisibleCells().map(cell => (
                        <TableCell key={cell.id} role="gridcell">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                    {subRow ? (
                      <TableRow role="row" data-testid="data-table-subrow">
                        <TableCell colSpan={colCount} className="bg-muted/30 p-0">
                          {subRow}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {mobileCard && (
        <div data-testid="data-table-mobile" className="space-y-2 lg:hidden">
          {isLoading ? (
            <div role="status" aria-live="polite" className="space-y-2 py-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-16 w-full animate-pulse rounded bg-muted" />
              ))}
              <span className="sr-only">Betöltés…</span>
            </div>
          ) : error ? (
            <p className="py-8 text-center text-destructive">{error}</p>
          ) : data.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">{emptyMessage}</p>
          ) : (
            mobileRows.map(row => (
              <React.Fragment key={row.id}>{mobileCard(row.original)}</React.Fragment>
            ))
          )}
        </div>
      )}

      {showPagination && !isLoading && (
        <DataTablePagination
          pageIndex={table.getState().pagination.pageIndex}
          pageCount={table.getPageCount()}
          rowCount={rowCount}
          canPrevious={table.getCanPreviousPage()}
          canNext={table.getCanNextPage()}
          onPrevious={() => table.previousPage()}
          onNext={() => table.nextPage()}
          labels={pageLabels}
        />
      )}
    </div>
  );
}

interface DataTablePaginationInternalProps {
  pageIndex: number;
  pageCount: number;
  rowCount?: number | undefined;
  canPrevious: boolean;
  canNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  labels: Required<DataTablePaginationLabels>;
}

function DataTablePagination({
  pageIndex,
  pageCount,
  rowCount,
  canPrevious,
  canNext,
  onPrevious,
  onNext,
  labels,
}: DataTablePaginationInternalProps) {
  return (
    <div className="flex items-center justify-between gap-2 px-2 py-3 text-sm">
      <span className="text-muted-foreground">
        {rowCount !== undefined ? labels.rowsTotal(rowCount) : null}
      </span>
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">
          {labels.pageOf(pageIndex + 1, Math.max(pageCount, 1))}
        </span>
        <button
          type="button"
          aria-label={labels.previous}
          disabled={!canPrevious}
          onClick={onPrevious}
          className="rounded border px-2 py-1 disabled:opacity-50"
        >
          ‹
        </button>
        <button
          type="button"
          aria-label={labels.next}
          disabled={!canNext}
          onClick={onNext}
          className="rounded border px-2 py-1 disabled:opacity-50"
        >
          ›
        </button>
      </div>
    </div>
  );
}
